// react
import { useEffect, useState } from 'react';

// i18n
import { useTranslation } from 'react-i18next';

// hooks
import { useFetch } from '../../../hooks/useFetch';

// router
import { useSearchParams } from 'react-router-dom';

// dialogs
import { DialogActions, DialogActionsProps } from '../DialogActions/DialogActions';

// forms
import { FormEditTransaction } from '../../forms/FormEditTransaction/FormEditTransaction';

// types
import { ActionButton, Transaction } from '../../../@types';

// utils
import { formatCurrency } from '../../../utils/currency';

// props
export interface DialogEditTransactionProps {
	isOpen: boolean;
	onClose: () => void;
	onSuccess?: () => void;
	transaction?: Transaction;
}

export const DialogEditTransaction = ({
	isOpen,
	onClose,
	onSuccess,
	transaction: transactionProp,
}: DialogEditTransactionProps) => {
	// session
	const accessToken = sessionStorage.getItem('accessToken');
	const idToken = sessionStorage.getItem('idToken');

	// hooks
	const [searchParams] = useSearchParams();
	const { t } = useTranslation();

	// params
	const paramDialogTransaction = searchParams.get('dialogTransaction');

	// state
	const [isLoading, setIsLoading] = useState(false);
	const [transaction, setTransaction] = useState<Transaction | null>(transactionProp || null);

	// fetch transaction by id if search param provided
	useFetch({
		isEnabled: Boolean(paramDialogTransaction && isOpen),
		url: `${process.env.REACT_APP_API_URL}/transactions/${paramDialogTransaction}?populate=category,component,contract,expenseType,fromAccount,fromEntity,payments,payments.conversions,payments.loan,tags,toAccount,toEntity`,
		options: {
			headers: {
				Authorization: `Bearer ${accessToken}`,
				User: String(idToken),
			},
		},
		onSuccess: (res) => {
			if (res.data && res.data[0]) {
				setTransaction(res.data[0]);
			}
		},
	});

	// vars
	const type = transaction?.type;
	const propsDialogActions: Partial<DialogActionsProps> = {
		actions: [
			{
				children: t('cancel'),
				onClick: onClose,
			},
		],
		title: {
			children: t('editPayment'),
			variant: 'h3',
		},
		subtitle: {
			children: transaction
				? formatCurrency({
						currency: transaction.currency,
						value: transaction.total,
					})
				: null,
		},
	};
	const actionSubmit: ActionButton = {
		children: t('savePayment'),
		form: 'form-edit-transaction',
		loading: isLoading,
		type: 'submit',
		variant: 'contained',
	};

	if (type === 'ADVANCE') {
		actionSubmit.children = t('saveAdvance');

		if (propsDialogActions.title) {
			propsDialogActions.title.children = t('editAdvance');
		}
	} else if (type === 'CASH_BENEFIT') {
		actionSubmit.children = t('saveCashBenefit');

		if (propsDialogActions.title) {
			propsDialogActions.title.children = t('editCashBenefit');
		}
	} else if (type === 'DIRECT') {
		actionSubmit.children = t('saveDirectPayment');

		if (propsDialogActions.title) {
			propsDialogActions.title.children = t('editDirectPayment');
		}
	} else if (type === 'INITIAL') {
		actionSubmit.children = t('saveInitialDeposit');

		if (propsDialogActions.title) {
			propsDialogActions.title.children = t('editInitialDeposit');
		}
	} else if (type === 'OPEX') {
		actionSubmit.children = t('saveOpEx');

		if (propsDialogActions.title) {
			propsDialogActions.title.children = t('editOpEx');
		}
	} else if (type === 'REIMBURSEMENT') {
		actionSubmit.children = t('saveReimbursement');

		if (propsDialogActions.title) {
			propsDialogActions.title.children = t('editReimbursement');
		}
	} else if (type === 'REPLENISHMENT') {
		actionSubmit.children = t('saveReplenishment');

		if (propsDialogActions.title) {
			propsDialogActions.title.children = t('editReplenishment');
		}
	} else if (type === 'SPECIAL') {
		actionSubmit.children = t('saveSpecialCommitment');

		if (propsDialogActions.title) {
			propsDialogActions.title.children = t('editSpecialCommitment');
		}
	}
	if (propsDialogActions.actions) {
		propsDialogActions.actions.push(actionSubmit);
	}

	// reset transaction on close
	useEffect(() => {
		if (!isOpen) {
			setTransaction(null);
		}
	}, [isOpen]);

	// early return if no transaction
	if (!transaction) {
		return <></>;
	}

	return (
		<DialogActions hasClose={true} isOpen={isOpen} onClose={onClose} {...propsDialogActions}>
			<FormEditTransaction
				onClose={onClose}
				onSuccess={onSuccess}
				setIsLoading={setIsLoading}
				transaction={transaction}
			/>
		</DialogActions>
	);
};
